// A compressed copy that fails to load (network, parse, decode, or textures missing once it has loaded) gives way to its
// original at once, and the fallback is reported (asset_compressed_fallback) to the app's telemetry: the viewer never
// sees a broken model when the original would load. No three.js here; the loaders pass their own load and check.
import type { CompressedCopy } from './modelFiles';

export const COMPRESSED_FALLBACK_EVENT = 'asset_compressed_fallback';

export type FallbackReason = 'network' | 'parse' | 'decode' | 'missing_textures';

export interface CompressedFallback {
  /** The asset type the copy belongs to. */
  assetId: string;
  /** The copy's asset_files row. */
  assetFileId?: string;
  reason: FallbackReason;
  /** What failed, without addresses. */
  detail?: string;
}

/** Where a fallback is reported: an event name and its properties. */
export type FallbackSink = (event: string, properties: Record<string, string>) => void;

// By name, so a telemetry client registering again (a remount) replaces itself rather than reporting twice
const sinks = new Map<string, FallbackSink>();

/** Reports every fallback to `sink` from now on, under `name`; returns the unregister. */
export function setFallbackSink(name: string, sink: FallbackSink): () => void {
  sinks.set(name, sink);
  return () => {
    if (sinks.get(name) === sink) sinks.delete(name);
  };
}

const ADDRESS = /\b[a-z][a-z0-9+.-]*:\/\/[^\s"'`)]+|\bblob:[^\s"'`)]+/gi;

/** An error's message without the addresses in it, which would otherwise decide its reason by a file's name. */
const messageOf = (error: unknown): string =>
  (error instanceof Error ? error.message : String(error ?? '')).replace(ADDRESS, '').replace(/\s+/g, ' ').trim();

/** Why loading a copy failed, from its error: its file never arrived, a decoder failed, or the file did not parse. */
export function fallbackReason(error: unknown): Exclude<FallbackReason, 'missing_textures'> {
  const message = messageOf(error);
  if (/failed to fetch|networkerror|load failed|responded with|network|timed? ?out|abort/i.test(message)) return 'network';
  if (/meshopt|ktx2|basis|transcod|malformed buffer|decod|webassembly|wasm/i.test(message)) return 'decode';
  return 'parse';
}

export function reportCompressedFallback(fallback: CompressedFallback): void {
  const properties: Record<string, string> = { asset_id: fallback.assetId, reason: fallback.reason };
  if (fallback.assetFileId) properties.asset_file_id = fallback.assetFileId;
  if (fallback.detail) properties.detail = fallback.detail.slice(0, 200);
  console.warn(`[compressedModels] ${fallback.reason} loading the compressed copy of ${fallback.assetId}; loading the original`, fallback.detail ?? '');
  for (const sink of sinks.values()) {
    try {
      sink(COMPRESSED_FALLBACK_EVENT, properties);
    } catch {
      // Telemetry never stands in the way of the original loading
    }
  }
}

// Copies that failed in this page: the original loads straight away the next time, without trying them again
const failedCopies = new Set<string>();

export const hasCopyFailed = (copy: CompressedCopy): boolean => failedCopies.has(copy.url);

/** Records a copy's failure and reports it, once per copy in this page. */
export function copyFailed(assetId: string, copy: CompressedCopy, reason: FallbackReason, detail?: string): void {
  if (failedCopies.has(copy.url)) return;
  failedCopies.add(copy.url);
  reportCompressedFallback({ assetId, assetFileId: copy.fileId, reason, detail });
}

/** Clears the failed copies; for tests. */
export function forgetFailedCopies(): void {
  failedCopies.clear();
}

/** The fields of a loaded glTF (three's GLTFLoader or three-stdlib's) the texture check reads. */
export interface LoadedGltfLike {
  parser?: {
    json?: { textures?: unknown[] };
    getDependencies?: (type: 'texture') => Promise<unknown[]>;
  };
}

/**
 * How many of a loaded glTF's textures did not load. GLTFLoader resolves a texture that failed (a KTX2 texture that
 * would not transcode) as null and loads the model without it, so this asks it for every texture; they are cached.
 */
export async function missingTextureCount(gltf: LoadedGltfLike): Promise<number> {
  const parser = gltf.parser;
  const expected = parser?.json?.textures?.length ?? 0;
  if (!expected || !parser?.getDependencies) return 0;
  try {
    const textures = await parser.getDependencies('texture');
    return textures.filter(texture => !texture).length;
  } catch {
    return expected;
  }
}

/** The check a loaded copy passes before it is shown: null when it is whole, else why not. */
export async function checkLoadedCopy(gltf: LoadedGltfLike): Promise<{ reason: FallbackReason; detail: string } | null> {
  const missing = await missingTextureCount(gltf);
  return missing > 0 ? { reason: 'missing_textures', detail: `${missing} texture(s) did not load` } : null;
}

export interface ModelSource {
  /** The asset type the model is. */
  assetId: string;
  /** The original. */
  url: string;
  /** The compressed copy to try first; none loads the original exactly as before. */
  copy: CompressedCopy | null;
}

export interface FallbackLoad<T> {
  load: (url: string) => Promise<T>;
  /** Checks a loaded copy (checkLoadedCopy for a glTF); null when it is whole. */
  check: (result: T) => Promise<{ reason: FallbackReason; detail: string } | null>;
  /** Frees a copy that loaded but failed its check. */
  dispose?: (result: T) => void;
}

/**
 * Loads a model's compressed copy, and its original at once when the copy fails in any way: the copy's failure is
 * reported, and only the original's own failure reaches the caller, as it would have without a copy.
 */
export async function loadWithFallback<T>(source: ModelSource, { load, check, dispose }: FallbackLoad<T>): Promise<T> {
  const { copy } = source;
  if (!copy || hasCopyFailed(copy)) return load(source.url);

  let result: T;
  try {
    result = await load(copy.url);
  } catch (error) {
    copyFailed(source.assetId, copy, fallbackReason(error), messageOf(error));
    return load(source.url);
  }

  let problem: { reason: FallbackReason; detail: string } | null;
  try {
    problem = await check(result);
  } catch (error) {
    problem = { reason: 'parse', detail: messageOf(error) };
  }
  if (!problem) return result;

  try {
    dispose?.(result);
  } catch {
    // A copy that will not free itself still gives way to its original
  }
  copyFailed(source.assetId, copy, problem.reason, problem.detail);
  return load(source.url);
}

/** A failure's detail for the report, without addresses. */
export const fallbackDetail = messageOf;
